import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from './api.js';
import { useRealtime } from './realtime.js';
import { useArena } from './arena/useArena.js';
import { usePrivateMessages } from './chat/usePrivateMessages.js';
import { t } from './i18n/index.js';
import { LoginView } from './views/LoginView.js';
import { ArenaView } from './views/ArenaView.js';
import { GameView } from './views/GameView.js';
import { AccountView } from './views/AccountView.js';
import { AdminView } from './views/AdminView.js';
import { BingoHostView } from './views/BingoHostView.js';
import { ChallengeLayer } from './components/ChallengeLayer.js';
import { GameResultDialog } from './components/GameResultDialog.js';
import { MusicButton, MuteButton } from './components/SoundSettingsCard.js';
import { Logo } from './components/Logo.js';
import { installAudioUnlock, playSound } from './audio/sounds.js';
import { installRadioUnlock, setRadioConfig, type RadioConfig } from './audio/radio.js';
import { isStaff, type ActiveGameView, type MeUser } from '../shared/protocol.js';
import { MaintenanceView } from './views/MaintenanceView.js';
import { PasswordChangeView } from './views/PasswordChangeView.js';
import { Footer } from './components/Footer.js';
import { ClosureBanner } from './components/ClosureBanner.js';
import { ModerationView } from './views/ModerationView.js';
import { TablesView } from './views/TablesView.js';
import { MultiGameView } from './views/MultiGameView.js';
import { MultiResultDialog } from './components/MultiResultDialog.js';
import { JoinView, type JoinMode } from './views/JoinView.js';
import { GuestView } from './views/GuestView.js';
import { MessagesView } from './views/MessagesView.js';
import { PmLoginNotice } from './components/PmLoginNotice.js';
import { GuestBadge } from './components/GuestBadge.js';
import { Coins } from './components/CoinIcon.js';
import { BlackjackView } from './games/blackjack/BlackjackView.js';
import { PokerView } from './games/poker/PokerView.js';
import { useCoins } from './coins.js';
import { guestSession } from './guestSession.js';
import { JOIN_ROUTES } from '../shared/registration.js';

/** While the server does not answer (e.g. during an update), it is retried this often. */
const OFFLINE_RETRY_MS = 10_000;

type Route = 'home' | 'tables' | 'messages' | 'account' | 'admin' | 'moderation' | 'bingo' | 'blackjack' | 'poker';

function routeFromHash(): Route {
  const h = location.hash.replace(/^#\/?/, '');
  if (h === 'viestit') return 'messages';
  if (h === 'blackjack' || h.startsWith('blackjack/')) return 'blackjack';
  if (h === 'pokeri') return 'poker';
  return h === 'tables' || h === 'account' || h === 'admin' || h === 'moderation' || h === 'bingo' ? h : 'home';
}

/** Public registration pages (#/liity/<token>, #/salasana/<token>, #/kutsu[/<code>]) and the guest page (#/vieras[/<code>]). */
type JoinRoute = { mode: JoinMode; param: string | null } | { mode: 'guest'; param: string | null };

function joinFromHash(): JoinRoute | null {
  const [head, param] = location.hash.replace(/^#\/?/, '').split('/');
  const mode: JoinMode | 'guest' | null =
    head === JOIN_ROUTES.link
      ? 'link'
      : head === JOIN_ROUTES.reset
        ? 'reset'
        : head === JOIN_ROUTES.code
          ? 'code'
          : head === JOIN_ROUTES.guest
            ? 'guest'
            : null;
  if (!mode) return null;
  try {
    return { mode, param: param ? decodeURIComponent(param) : null } as JoinRoute;
  } catch {
    return { mode, param: null } as JoinRoute;
  }
}

/** After registering: reload without the link in the address (and in the browser history). */
function enterArena() {
  history.replaceState(null, '', location.pathname);
  location.reload();
}

export function App() {
  const [user, setUser] = useState<MeUser | null | undefined>(undefined);
  const [route, setRoute] = useState<Route>(routeFromHash());
  const [join, setJoin] = useState(joinFromHash());
  // The server did not answer (service restarting, system maintenance).
  const [offline, setOffline] = useState(false);
  guestSession.guest = Boolean(user && user.guestUntil !== null);
  // A guest's visit ended (time up or logged out): a thank-you note on the login page.
  const [loginNotice, setLoginNotice] = useState<string | null>(null);
  const wasGuest = useRef(false);
  useEffect(() => {
    if (user) {
      wasGuest.current = user.guestUntil !== null;
      setLoginNotice(null);
    } else if (user === null && wasGuest.current) {
      wasGuest.current = false;
      setLoginNotice(t.guest.ended);
    }
  }, [user]);

  const refreshUser = useCallback(() => {
    api<{ user: MeUser }>('GET', '/api/auth/me')
      .then((r) => {
        setOffline(false);
        setUser(r.user);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setOffline(false);
          setUser(null);
        } else {
          setOffline(true);
        }
      });
  }, []);

  useEffect(() => {
    refreshUser();
    const onHash = () => {
      setRoute(routeFromHash());
      setJoin(joinFromHash());
    };
    addEventListener('hashchange', onHash);
    return () => removeEventListener('hashchange', onHash);
  }, []);

  // Not reachable before the login state is known: retry until the service is back.
  useEffect(() => {
    if (!offline || user !== undefined) return;
    const timer = setInterval(refreshUser, OFFLINE_RETRY_MS);
    return () => clearInterval(timer);
  }, [offline, user]);

  // The arena connection opens only after a temporary password has been changed.
  const realtime = useRealtime(Boolean(user && !user.mustChangePassword), refreshUser);

  // Automatic logout (2026-10-02): the login page tells why.
  useEffect(() => {
    const s = realtime.socket;
    if (!s) return;
    const onEnded = ({ reason }: { reason: 'idle' | 'away' }) => setLoginNotice(reason === 'idle' ? t.login.loggedOutIdle : t.login.loggedOutAway);
    s.on('session:ended', onEnded);
    return () => {
      s.off('session:ended', onEnded);
    };
  }, [realtime.socket]);
  const arena = useArena(realtime.socket, user?.id ?? 0);
  const pm = usePrivateMessages(realtime.socket, user?.id ?? 0);

  // Sitting down at a game table opens the table page; a starting game opens the game.
  const tableId = arena.me?.table?.id ?? null;
  const multiId = arena.me?.multiGame?.id ?? null;
  useEffect(() => {
    if (tableId) location.hash = '#/tables';
  }, [tableId]);
  useEffect(() => {
    if (multiId) location.hash = '';
  }, [multiId]);

  // The final position of an ended game stays on screen behind the result (user request
  // 2026-09-29); the player can close the result to look at it.
  const lastGame = useRef<ActiveGameView | null>(null);
  const [resultMinimized, setResultMinimized] = useState(false);
  useEffect(() => {
    setResultMinimized(false);
  }, [arena.gameResult?.id]);

  // Audio can only start after a user gesture.
  useEffect(() => {
    const removeUnlock = installAudioUnlock();
    const removeRadio = installRadioUnlock();
    return () => {
      removeUnlock();
      removeRadio();
    };
  }, []);

  // The radio of this player (decision 2026-09-29): fetched on every (re)connection, so that a
  // new music permission from the administrator takes effect right away.
  const connected = realtime.state === 'connected';
  useEffect(() => {
    if (!user || user.mustChangePassword) return setRadioConfig(null);
    if (!connected) return;
    api<{ radio: RadioConfig | null }>('GET', '/api/radio')
      .then((r) => setRadioConfig(r.radio))
      .catch(() => {});
  }, [user?.id, connected]);

  // Connection lost / restored sounds.
  const prevConn = useRef(realtime.state);
  useEffect(() => {
    const prev = prevConn.current;
    prevConn.current = realtime.state;
    if (prev === 'connected' && realtime.state === 'disconnected') playSound('connectionLost');
    if (prev === 'disconnected' && realtime.state === 'connected') playSound('connectionRestored');
  }, [realtime.state]);

  const logout = async () => {
    // A guest cannot come back with the same code (decision 2026-09-29).
    if (user?.guestUntil != null && !confirm(t.guest.logoutConfirm)) return;
    await api('POST', '/api/auth/logout').catch(() => {});
    setUser(null);
    location.hash = '';
  };

  if (join?.mode === 'guest') return <GuestView key={location.hash} param={join.param} onLogin={enterArena} />;
  if (join) return <JoinView key={location.hash} mode={join.mode} param={join.param} onLogin={enterArena} />;

  if (user === undefined) {
    return offline ? (
      <MaintenanceView title={t.closure.offlineTitle} message={t.closure.offlineMessage} endsAt={null} />
    ) : (
      <div class="splash" aria-busy="true" />
    );
  }
  if (user === null) return <LoginView onLogin={setUser} notice={loginNotice} />;
  if (user.mustChangePassword) return <PasswordChangeView user={user} onDone={refreshUser} onLogout={logout} />;

  const activeGame = arena.me?.activeGame ?? null;
  if (activeGame) lastGame.current = activeGame;
  const endedGame =
    !activeGame && arena.gameResult && lastGame.current?.id === arena.gameResult.id ? lastGame.current : null;
  const closeResult = () => {
    arena.clearGameResult();
    location.hash = '';
  };
  const resultDialog = arena.gameResult && !(endedGame && resultMinimized) && (
    <GameResultDialog
      result={arena.gameResult}
      userId={user.id}
      maintenance={arena.closure?.phase === 'closed'}
      onClose={closeResult}
      onShowBoard={
        endedGame && route === 'home' && !(arena.closure?.closed && user.role !== 'admin')
          ? () => setResultMinimized(true)
          : undefined
      }
    />
  );

  const multiDialog = arena.multiResult && (
    <MultiResultDialog
      result={arena.multiResult}
      userId={user.id}
      maintenance={arena.closure?.phase === 'closed'}
      onClose={() => {
        arena.clearMultiResult();
        location.hash = '';
      }}
    />
  );

  // Arena closed (decision 2026-09-27): players see the maintenance page (running games are
  // aborted when the break begins; the result is shown on top). The administrator uses the
  // arena normally.
  const closure = arena.closure;
  const closedForMe = Boolean(closure?.closed) && user.role !== 'admin';
  if (closedForMe && arena.me && !arena.me.activeGame && !arena.me.multiGame) {
    return (
      <>
        <MaintenanceView message={closure!.message} endsAt={closure!.endsAt} clockOffset={realtime.clockOffset}>
          <button class="btn btn-ghost btn-small" onClick={logout}>
            {t.nav.logout}
          </button>
        </MaintenanceView>
        {resultDialog}
        {multiDialog}
      </>
    );
  }

  const current: Route =
    (route === 'admin' && user.role !== 'admin') ||
    (route === 'moderation' && !isStaff(user.role)) ||
    (route === 'bingo' && !user.bingoHost && user.role !== 'admin')
      ? 'home'
      : route;
  const multiGame = arena.me?.multiGame ?? null;
  const playing = activeGame !== null || multiGame !== null;
  const gameOnScreen = activeGame ?? endedGame;
  const navItem = (r: Route, label: string) => (
    <a href={`#/${r === 'home' ? '' : r}`} class={current === r ? 'active' : ''}>
      {label}
    </a>
  );
  const connTitle =
    `${t.status.realtime}: ${t.status[realtime.state]}` +
    (realtime.state === 'connected' && realtime.latency !== null ? ` · ${t.status.latency} ${realtime.latency} ms` : '');

  return (
    <div class={`shell ${(current === 'home' && (!gameOnScreen || multiGame)) || current === 'blackjack' || current === 'poker' ? 'shell-wide' : ''}`}>
      <header class="topbar">
        <a class="brand" href="#/">
          <Logo />
        </a>
        <nav>
          {navItem('home', playing ? t.nav.game : t.nav.arena)}
          <a href="#/tables" class={current === 'tables' || current === 'blackjack' || current === 'poker' ? 'active' : ''}>
            {t.nav.tables}
            {arena.tables.length > 0 && (
              <span class="nav-badge" title={t.tables.navBadge(arena.tables.length)}>
                {arena.tables.length}
              </span>
            )}
          </a>
          <a href="#/viestit" class={current === 'messages' ? 'active' : ''}>
            {t.nav.messages}
            {pm.totalUnread > 0 && (
              <span class="nav-badge unread" title={t.nav.messagesUnread(pm.totalUnread)}>
                {pm.totalUnread}
              </span>
            )}
          </a>
          {navItem('account', t.nav.account)}
          {isStaff(user.role) && navItem('moderation', t.nav.moderation)}
          {user.bingoHost && user.role !== 'admin' && navItem('bingo', t.nav.bingo)}
          {user.role === 'admin' && navItem('admin', t.nav.admin)}
        </nav>
        <div class="who">
          <span class={`conn conn-${realtime.state}`} title={connTitle} aria-label={connTitle} />
          <span class="name">{user.displayName}</span>
          {user.guestUntil === null && <CoinBadge />}
          {user.guestUntil !== null && <GuestBadge until={user.guestUntil} onEnded={refreshUser} />}
          <MusicButton />
          <MuteButton />
          <button class="btn btn-ghost" onClick={logout}>
            {t.nav.logout}
          </button>
        </div>
      </header>
      {realtime.state === 'disconnected' && <div class="conn-banner">{t.status.connectionLost}</div>}
      {pm.loginUnread && current !== 'messages' && <PmLoginNotice unread={pm.loginUnread} onDismiss={pm.dismissLoginUnread} />}
      {closure && <ClosureBanner closure={closure} clockOffset={realtime.clockOffset} />}
      {closure?.closed && user.role === 'admin' && (
        <div class="closure-banner closed" role="status">
          🔒 {t.closure.adminClosed} <a href="#/admin">{t.closure.adminLink}</a>
        </div>
      )}
      <main class="content">
        {current === 'home' &&
          (multiGame ? (
            <MultiGameView game={multiGame} realtime={realtime} myId={user.id} role={user.role} />
          ) : activeGame ? (
            <GameView game={activeGame} realtime={realtime} role={user.role} />
          ) : endedGame && arena.gameResult ? (
            <GameView
              game={endedGame}
              realtime={realtime}
              role={user.role}
              ended={{
                result: arena.gameResult,
                userId: user.id,
                maintenance: arena.closure?.phase === 'closed',
                onLeave: closeResult,
              }}
            />
          ) : (
            <ArenaView user={user} realtime={realtime} arena={arena} pm={pm} />
          ))}
        {current === 'tables' && <TablesView user={user} realtime={realtime} arena={arena} />}
        {current === 'blackjack' && <BlackjackView />}
        {current === 'poker' && <PokerView />}
        {current === 'messages' && <MessagesView me={user} pm={pm} players={arena.players} restrictions={arena.me?.restrictions ?? null} />}
        {current === 'account' && <AccountView user={user} />}
        {current === 'admin' && <AdminView me={user} />}
        {current === 'bingo' && <BingoHostView />}
        {current === 'moderation' && <ModerationView me={user} socket={realtime.socket} />}
      </main>
      <ChallengeLayer realtime={realtime} arena={arena} userId={user.id} showPmNotices={current !== 'home' || playing} />
      {resultDialog}
      {multiDialog}
      <Footer />
    </div>
  );
}

/** The player's AreCoin balance in the top bar (2026-10-01); opens the account page. */
function CoinBadge() {
  const coins = useCoins(true);
  if (!coins) return null;
  return (
    <a class={`coin-badge ${coins.canRefill ? 'can-refill' : ''}`} href="#/account" title={coins.canRefill ? 'AreCoin – päivän täydennys odottaa!' : 'AreCoin-saldo'}>
      <Coins amount={coins.balance} size={18} />
    </a>
  );
}
